/**
 * The roster as a table: every farmer on the record with their stage, phone,
 * pin check and assignee; filter, open the card, export what is shown.
 */
import { useEffect, useMemo, useState } from 'react';
import type { Holder, Segment, Stage } from '@shared/model';
import { SEGMENT_COLOR, STAGES, STAGE_LABEL, prettyPhone } from '@shared/model';
import { FarmerCard, fmtDate } from '../components/FarmerCard';
import { StagePill } from '../components/StagePill';
import { StateBlock } from '../components/StateBlock';
import { useRoster } from '../state/RosterContext';

const PAGE = 200;

export function Farmers({ query }: { query: URLSearchParams }): JSX.Element {
  const { holders, rows, villages, staff, farmIndex } = useRoster();
  const [village, setVillage] = useState(query.get('village') ?? 'all');
  const [stage, setStage] = useState<Stage | 'all' | 'attention'>(query.get('attention') ? 'attention' : 'all');
  const [segment, setSegment] = useState<Segment | 'all' | 'none'>('all');
  const [assignee, setAssignee] = useState('all');
  const [block, setBlock] = useState<'all' | 'yes'>('all');
  const [q, setQ] = useState('');
  const [limit, setLimit] = useState(PAGE);
  const [open, setOpen] = useState<string | null>(query.get('holder'));

  useEffect(() => { setLimit(PAGE); }, [village, stage, segment, assignee, block, q]);

  const filtered = useMemo(() => {
    if (holders.status !== 'ready') return [] as Holder[];
    const s = q.trim().toLowerCase();
    const digits = s.replace(/\D/g, '');
    return holders.data.filter((h) => {
      if (village !== 'all' && h.village !== village) return false;
      if (segment === 'none' ? h.segment !== null : segment !== 'all' && h.segment !== segment) return false;
      if (block === 'yes' && !h.in_block) return false;
      const r = rows.get(h.id);
      const st = r?.stage ?? 'target';
      if (stage === 'attention') {
        if (!r || r.pin_lat == null || r.pin_inside_own) return false;
      } else if (stage !== 'all' && st !== stage) return false;
      if (assignee !== 'all' && (r?.assigned_to ?? '') !== assignee) return false;
      if (s) {
        if (!(h.name.toLowerCase().includes(s) || h.relation.toLowerCase().includes(s) || h.farms.some((f) => f.khasra.toLowerCase() === s) || (digits.length >= 4 && (r?.phone ?? '').includes(digits)))) return false;
      }
      return true;
    });
  }, [holders, rows, village, stage, segment, assignee, block, q]);

  const exportCsv = (): void => {
    const head = ['holder_id', 'name', 'relation', 'village', 'khasras', 'farm_count', 'acres', 'segment', 'in_block', 'farm_pin_lat', 'farm_pin_lon', 'stage', 'phone', 'wa_pin_lat', 'wa_pin_lon', 'wa_pin_check', 'boundary_source', 'assigned_to', 'last_message_at', 'notes'];
    const esc = (v: unknown): string => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const body = filtered.map((h) => {
      const r = rows.get(h.id);
      const check = !r || r.pin_lat == null ? '' : r.pin_pid ? (r.pin_inside_own ? 'inside own khasra' : `inside khasra ${farmIndex.get(r.pin_pid)?.khasra ?? r.pin_pid} (another holder)`) : `outside the record (${r.pin_distance_m ?? '?'} m)`;
      return [
        h.id, h.name, h.relation, h.village, h.farms.map((f) => f.khasra).join(' | '), h.farm_count, h.acres.toFixed(3), h.segment ?? '', h.in_block ? 'yes' : 'no',
        h.pin[0].toFixed(6), h.pin[1].toFixed(6), r?.stage ?? 'target', r?.phone ?? '', r?.pin_lat ?? '', r?.pin_lon ?? '', check, r?.boundary_source ?? '', r?.assigned_to ?? '', r?.last_message_at ?? '', r?.notes ?? '',
      ].map(esc).join(',');
    });
    const csv = [head.join(','), ...body].join('\n');
    const url = URL.createObjectURL(new Blob([`﻿${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ultratech-farmers-${village === 'all' ? 'all' : village.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (holders.status !== 'ready') return <div className="page"><StateBlock state={holders} title="Farmer roster" /></div>;
  const openHolder = open ? holders.data.find((h) => h.id === open) ?? null : null;
  const acresShown = filtered.reduce((a, h) => a + h.acres, 0);

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1>Farmers</h1>
          <p>{filtered.length.toLocaleString('en-IN')} of {holders.data.length.toLocaleString('en-IN')} farmers shown · named on {Math.round(acresShown).toLocaleString('en-IN')} acres</p>
        </div>
        <span className="spacer" />
        <button type="button" className="btn-primary" disabled={!filtered.length} onClick={exportCsv}>Export shown (CSV)</button>
      </div>

      <div className="filters">
        <label>Search<input value={q} onChange={(e) => setQ(e.target.value)} placeholder="name, father, khasra, phone" style={{ minWidth: 240 }} /></label>
        <label>Village<select value={village} onChange={(e) => setVillage(e.target.value)}><option value="all">All</option>{villages.map((v) => <option key={v.village} value={v.village}>{v.village} ({v.holders})</option>)}</select></label>
        <label>Stage<select value={stage} onChange={(e) => setStage(e.target.value as Stage | 'all' | 'attention')}><option value="all">All</option><option value="attention">Needs a visit (pin not in own khasra)</option>{STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}</select></label>
        <label>Segment<select value={segment} onChange={(e) => setSegment(e.target.value as Segment | 'all' | 'none')}><option value="all">All</option><option value="marginal">Marginal</option><option value="small">Small</option><option value="medium">Medium</option><option value="none">Not matched</option></select></label>
        <label>Block<select value={block} onChange={(e) => setBlock(e.target.value as 'all' | 'yes')}><option value="all">All farms</option><option value="yes">1,000-acre block only</option></select></label>
        <label>Assigned to<select value={assignee} onChange={(e) => setAssignee(e.target.value)}><option value="all">Anyone</option><option value="">Unassigned</option>{staff.map((s) => <option key={s.email} value={s.email}>{s.name}</option>)}</select></label>
      </div>

      <section className="card card--pad" style={{ overflowX: 'auto' }}>
        <table className="vtable">
          <thead>
            <tr><th>Farmer</th><th>Village</th><th>Khasras</th><th className="num">Acres</th><th>Segment</th><th>Stage</th><th>Phone</th><th>WhatsApp pin</th><th>Last message</th><th>Assigned</th></tr>
          </thead>
          <tbody>
            {filtered.slice(0, limit).map((h) => {
              const r = rows.get(h.id);
              return (
                <tr key={h.id} className={`is-click ${open === h.id ? 'is-on' : ''}`} onClick={() => setOpen(h.id)}>
                  <td>{h.name}{h.relation ? <div className="muted">{h.relation}</div> : null}</td>
                  <td>{h.village}</td>
                  <td>{h.farms.slice(0, 3).map((f) => f.khasra).join(', ')}{h.farms.length > 3 ? <span className="muted"> +{h.farms.length - 3}</span> : null}{h.in_block ? <span className="muted"> · block</span> : null}</td>
                  <td className="num">{h.acres.toFixed(h.acres < 10 ? 2 : 1)}</td>
                  <td>{h.segment ? <><span className="seg-chip" style={{ background: SEGMENT_COLOR[h.segment] }} />{h.segment}</> : <span className="muted">—</span>}</td>
                  <td><StagePill stage={r?.stage ?? 'target'} small /></td>
                  <td className="mono">{r?.phone ? prettyPhone(r.phone) : <span className="muted">—</span>}</td>
                  <td>{!r || r.pin_lat == null ? <span className="muted">—</span> : r.pin_pid ? (r.pin_inside_own ? <span className="ok">✓ own khasra</span> : <span className="warn">⚠ khasra {farmIndex.get(r.pin_pid)?.khasra ?? r.pin_pid}</span>) : <span className="bad">✗ outside · {r.pin_distance_m ?? '?'} m</span>}</td>
                  <td className="muted">{fmtDate(r?.last_message_at)}</td>
                  <td className="muted">{r?.assigned_to ? r.assigned_to.split('@')[0] : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length > limit ? (
          <div className="btn-row" style={{ marginTop: 12 }}>
            <span className="muted" style={{ fontSize: 12 }}>{limit.toLocaleString('en-IN')} of {filtered.length.toLocaleString('en-IN')} shown</span>
            <span className="btn-row__spacer" />
            <button type="button" className="btn-line" onClick={() => setLimit((l) => l + PAGE * 2)}>Show more</button>
          </div>
        ) : null}
      </section>

      {openHolder ? (
        <div className="drawer">
          <FarmerCard holder={openHolder} onClose={() => setOpen(null)} onShowFarm={(_pid) => { window.location.hash = `#/map/${openHolder.id}`; }} />
        </div>
      ) : null}
    </div>
  );
}
